/**
 * smc/fvg.ts
 * Fair Value Gap detection + freshness / mitigation logic.
 */
import { Candle, FairValueGap } from '../../../../types';
import { SMCThresholds } from './types';

export function detectFVGs(
  candles: Candle[],
  thresholds: SMCThresholds
): FairValueGap[] {
  const fvgs: FairValueGap[] = [];
  const minSizePct = thresholds.fvgMinSize / 100;

  for (let i = 2; i < candles.length; i++) {
    const prev2 = candles[i - 2];
    const c     = candles[i];

    // Bullish FVG: gap between high of prev2 and low of current
    if (c.low > prev2.high) {
      const top    = c.low;
      const bottom = prev2.high;
      const size   = top - bottom;
      if (size / c.close >= minSizePct) {
        fvgs.push({
          id:           `fvg_bull_${i}`,
          dir:          'bullish',
          top, bottom,
          midpoint:     (top + bottom) / 2,
          candleIdx:    i - 1,
          ts:           candles[i - 1].ts,
          status:       'open',
          filled:       0,
          size,
          significance: size / c.close > minSizePct * 3 ? 'major' : 'minor',
        });
      }
    }

    // Bearish FVG: gap between low of prev2 and high of current
    if (c.high < prev2.low) {
      const top    = prev2.low;
      const bottom = c.high;
      const size   = top - bottom;
      if (size / c.close >= minSizePct) {
        fvgs.push({
          id:           `fvg_bear_${i}`,
          dir:          'bearish',
          top, bottom,
          midpoint:     (top + bottom) / 2,
          candleIdx:    i - 1,
          ts:           candles[i - 1].ts,
          status:       'open',
          filled:       0,
          size,
          significance: size / c.close > minSizePct * 3 ? 'major' : 'minor',
        });
      }
    }
  }

  // Update fill status + freshness grading
  for (const fvg of fvgs) {
    let touchCount = 0;
    for (let j = fvg.candleIdx + 2; j < candles.length; j++) {
      const c = candles[j];
      if (fvg.dir === 'bullish') {
        if (c.low <= fvg.top) {
          touchCount++;
          const fillDepth = Math.min(fvg.top - c.low, fvg.size);
          fvg.filled = Math.min(1, fillDepth / fvg.size);
          fvg.status = fvg.filled >= 0.95 ? 'mitigated' : 'partially_mitigated';
        }
      } else {
        if (c.high >= fvg.bottom) {
          touchCount++;
          const fillDepth = Math.min(c.high - fvg.bottom, fvg.size);
          fvg.filled = Math.min(1, fillDepth / fvg.size);
          fvg.status = fvg.filled >= 0.95 ? 'mitigated' : 'partially_mitigated';
        }
      }
    }
    // Freshness grading per spec:
    //   fresh   = untouched (weight 1.0)
    //   mild    = 1 touch <50% fill (weight 0.7)
    //   degraded= ≥2 touches OR >50% fill (weight 0.4)
    //   invalid = full fill OR >3 touches (weight 0.0 — excluded)
    type FVGGrade = 'fresh' | 'mild' | 'degraded' | 'invalid';
    const FVG_WEIGHT: Record<FVGGrade, number> = { fresh: 1.0, mild: 0.7, degraded: 0.4, invalid: 0.0 };
    let fvgGrade: FVGGrade;
    if (fvg.status === 'mitigated' || touchCount > 3) {
      fvgGrade = 'invalid';
    } else if (fvg.filled > 0.5 || touchCount >= 2) {
      fvgGrade = 'degraded';
    } else if (touchCount === 1 && fvg.filled < 0.5) {
      fvgGrade = 'mild';
    } else {
      fvgGrade = 'fresh';
    }
    const fvgAge = candles.length - 1 - fvg.candleIdx;
    if (fvgGrade === 'fresh' && fvgAge > 20) fvgGrade = 'mild';
    const ext = fvg as FairValueGap & {
      freshness?: string; touchCount?: number;
      fvgGrade?: FVGGrade; fvgWeight?: number; fvgAge?: number;
    };
    ext.freshness  = fvgGrade;
    ext.fvgGrade   = fvgGrade;
    ext.fvgWeight  = FVG_WEIGHT[fvgGrade];
    ext.touchCount = touchCount;
    ext.fvgAge     = fvgAge;
  }

  // Keep only non-invalid FVGs, most recent first
  const openFVGs = fvgs
    .filter(f => {
      if (f.status === 'mitigated') return false;
      const ext = f as FairValueGap & { fvgGrade?: string };
      return ext.fvgGrade !== 'invalid';
    })
    .sort((a, b) => b.candleIdx - a.candleIdx)
    .slice(0, thresholds.maxFVGs);

  // Guarantee every surviving FVG has an explicit fvgWeight
  type FVGWeighted = FairValueGap & { fvgWeight?: number; fvgGrade?: string; freshness?: string };
  for (const f of openFVGs) {
    const fw = f as FVGWeighted;
    if (fw.fvgWeight === undefined || fw.fvgWeight === null) {
      fw.fvgWeight = 0.4;
      fw.fvgGrade  = fw.fvgGrade ?? 'degraded';
      fw.freshness = fw.freshness ?? 'degraded';
    }
  }

  return openFVGs;
}
